import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { ArrowLeft, Search, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BlockchainDetective = () => {
  const navigate = useNavigate();
  const { fetchProfile } = useAuth();
  const [gameStarted, setGameStarted] = useState(false);
  const [currentCase, setCurrentCase] = useState(0);
  const [answer, setAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  const cases = [
    {
      title: 'The Missing Transaction',
      description: 'A user claims they sent 5 BTC, but it never arrived. The transaction hash is 0x1234...5678. What could be the issue?',
      clues: ['Transaction shows 0 confirmations', 'Gas fee was set to 0', 'Network is congested'],
      correctAnswer: 'insufficient gas fee',
      hints: ['Check the gas fee', 'Was enough fee paid to miners?']
    },
    {
      title: 'The Double Spend Mystery',
      description: 'Two transactions claim to spend the same coins. How is this resolved?',
      clues: ['Both transactions are in mempool', 'Only one can be included in a block', 'Miners choose higher fee'],
      correctAnswer: 'blockchain consensus',
      hints: ['Think about how blocks are created', 'What determines which transaction wins?']
    },
    {
      title: 'The Smart Contract Bug',
      description: 'Users funds are locked in a contract. What security flaw allowed this?',
      clues: ['Contract has no withdraw function', 'Owner lost private key', 'Reentrancy vulnerability'],
      correctAnswer: 'reentrancy',
      hints: ['Common smart contract vulnerability', 'Allows repeated withdrawals']
    }
  ];

  const handleSubmit = () => {
    const currentCaseData = cases[currentCase];
    if (answer.toLowerCase().includes(currentCaseData.correctAnswer)) {
      setScore(score + 300);
      toast.success('Case solved! +300 points');
      if (currentCase < cases.length - 1) {
        setTimeout(() => {
          setCurrentCase(currentCase + 1);
          setAnswer('');
        }, 2000);
      } else {
        setTimeout(() => endGame(), 2000);
      }
    } else {
      toast.error('Incorrect. Try again!');
    }
  };

  const endGame = async () => {
    setGameOver(true);
    try {
      const response = await axios.post(`${API}/games/submit`, {
        game_type: 'detective',
        score: score,
        time_taken: 0
      });
      toast.success(`Investigation complete! Earned ${response.data.xp_gained} XP`);
      await fetchProfile();
    } catch (error) {
      console.error('Failed to submit score', error);
    }
  };

  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-start-screen">
            <Search className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Blockchain Detective</h1>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Solve blockchain mysteries by analyzing clues and finding the root cause. Each solved case earns 300 points!
            </p>
            <Button
              onClick={() => setGameStarted(true)}
              data-testid="start-game-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 hover:shadow-green-glow uppercase tracking-wide px-8 py-6 text-base h-auto"
            >
              Start Investigation
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              className="mt-4 text-zinc-400 hover:text-primary"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-over-screen">
            <Trophy className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Cases Solved!</h1>
            <div className="mb-6">
              <div className="text-5xl font-mono font-bold text-primary mb-2" data-testid="final-score">{score}</div>
              <div className="text-zinc-400">Total Score</div>
            </div>
            <Button
              onClick={() => window.location.reload()}
              data-testid="play-again-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 uppercase tracking-wide px-8 py-3 mb-3 w-full"
            >
              Investigate Again
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              className="text-zinc-400 hover:text-primary w-full"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const caseData = cases[currentCase];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/games')}
          data-testid="back-to-games-button"
          className="mb-4 text-zinc-400 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Exit Investigation
        </Button>

        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <span className="text-xs font-mono uppercase tracking-widest text-primary/80">Case {currentCase + 1} of {cases.length}</span>
            <div className="text-2xl font-mono font-bold text-primary" data-testid="game-score">{score}</div>
          </div>
          <h1 className="text-3xl font-grotesk font-bold text-white mb-2">{caseData.title}</h1>
        </div>

        <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-8 mb-6">
          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-3">Case Description</h3>
          <p className="text-zinc-300 mb-6 leading-relaxed">{caseData.description}</p>

          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-3">Clues</h3>
          <div className="space-y-2 mb-6">
            {caseData.clues.map((clue, index) => (
              <div key={index} className="flex items-start space-x-2">
                <span className="text-primary font-mono text-sm mt-0.5">•</span>
                <span className="text-zinc-400 text-sm">{clue}</span>
              </div>
            ))}
          </div>

          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-3">Your Analysis</h3>
          <Input
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="What's your conclusion?"
            data-testid="answer-input"
            className="mb-4 bg-black/50 border-zinc-800 focus:border-primary text-white"
          />
          
          <Button
            onClick={handleSubmit}
            disabled={!answer}
            data-testid="submit-answer-button"
            className="bg-primary text-black font-bold hover:bg-primary/90 uppercase tracking-wide px-8 py-3 w-full"
          >
            Submit Analysis
          </Button>
        </div>

        <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6">
          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-3">Hints</h3>
          {caseData.hints.map((hint, index) => (
            <div key={index} className="text-zinc-500 text-sm mb-1">💡 {hint}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlockchainDetective;
